// src/services/entidadesService.js - Versión Mejorada
import { apiClient } from './api';
import { createLogger } from '../utils/logger';

const logger = createLogger('API');

/**
 * Servicio para manejar entidades (personas o empresas identificadas por RUT)
 */
export const entidadesService = {
  /**
   * Obtener lista de entidades
   * @param {boolean} soloActivas - Filtrar solo entidades activas
   * @returns {Promise<Array>} - Promesa con la lista de entidades
   */
  async getEntidades(soloActivas = true) {
    return apiClient.get(`/rcv/entidades?activas_only=${soloActivas}`);
  },

  /**
   * Buscar entidades por término de búsqueda
   * @param {string} searchTerm - Término para buscar (RUT o nombre)
   * @param {number} limit - Límite de resultados a devolver
   * @returns {Promise<Array>} - Promesa con la lista de entidades que coinciden
   */
  async buscarEntidades(searchTerm, limit = 10) {
    try {
      // Limpiar y validar el término de búsqueda
      const termino = String(searchTerm).trim();
      
      if (!termino || termino.length < 2) {
        logger.warn('Término de búsqueda muy corto:', termino);
        return [];
      }

      logger.debug('Buscando entidades con término:', termino, 'limit:', limit);
      
      const response = await apiClient.get(
        `/rcv/entidades/search?search_term=${encodeURIComponent(termino)}&limit=${limit}`
      );
      
      logger.debug('Respuesta del servidor:', {
        termino,
        cantidad: response?.length || 0,
        entidades: response?.map(e => ({ id: e.id, nombre: e.nombre, rut: e.rut, dv: e.dv })) || []
      });
      
      return response || [];
    } catch (error) {
      console.error('Error en búsqueda de entidades:', error);
      
      // Si es un error 404 o similar, devolver array vacío en lugar de lanzar error
      if (error.response?.status === 404 || error.response?.status === 400) {
        logger.debug('Error 404/400 - devolviendo array vacío');
        return [];
      }
      
      throw new Error(`Error al buscar entidades: ${error.message}`);
    }
  },

  /**
   * Buscar entidad específica por RUT
   * @param {string} rut - Número de RUT sin DV
   * @param {string} dv - Dígito verificador
   * @returns {Promise<Object|null>} - Promesa con la entidad encontrada o null
   */
  async buscarPorRut(rut, dv) {
    try {
      if (!rut || !dv) {
        return null;
      }

      logger.debug('Buscando entidad por RUT:', { rut, dv });

      // Estrategia múltiple de búsqueda
      const terminosBusqueda = [
        `${rut}${dv}`,        // RUT completo: "778971224"
        rut,                  // Solo RUT: "77897122"
        `${rut}-${dv}`,       // Con guión: "77897122-4"
        this.formatearRutParaBusqueda(rut, dv) // Con puntos: "77.897.122-4"
      ];

      for (const termino of terminosBusqueda) {
        logger.debug('Probando búsqueda con término:', termino);
        
        const entidades = await this.buscarEntidades(termino, 5);
        
        if (entidades.length > 0) {
          // Buscar coincidencia exacta por RUT y DV
          const entidadCoincidente = entidades.find(entidad => {
            const rutCoincide = entidad.rut?.toString() === rut.toString();
            const dvCoincide = entidad.dv?.toString().toUpperCase() === dv.toString().toUpperCase();
            
            logger.debug('Verificando entidad:', {
              entidad: entidad.nombre,
              rutEntidad: entidad.rut,
              dvEntidad: entidad.dv,
              rutBuscado: rut,
              dvBuscado: dv,
              rutCoincide,
              dvCoincide
            });
            
            return rutCoincide && dvCoincide;
          });
          
          if (entidadCoincidente) {
            logger.info('Entidad encontrada:', entidadCoincidente.nombre);
            return entidadCoincidente;
          }
        }
      }

      logger.debug('No se encontró entidad para RUT:', rut, dv);
      return null;
    } catch (error) {
      console.error('Error en búsqueda por RUT:', error);
      return null;
    }
  },

  /**
   * Formatear RUT para búsqueda con puntos y guión
   * @param {string} rut - Número de RUT
   * @param {string} dv - Dígito verificador
   * @returns {string} - RUT formateado para búsqueda
   */
  formatearRutParaBusqueda(rut, dv) {
    if (!rut || !dv) return '';
    
    const rutStr = rut.toString();
    const cuerpoFormateado = rutStr.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    
    return `${cuerpoFormateado}-${dv}`;
  },

  /**
   * Obtener una entidad por su ID
   * @param {number} entidadId - ID de la entidad
   * @returns {Promise<Object>} - Promesa con los datos de la entidad
   */
  async getEntidad(entidadId) {
    return apiClient.get(`/rcv/entidades/${entidadId}`);
  },

  /**
   * Crear una nueva entidad
   * @param {Object} entidad - Datos de la entidad a crear
   * @returns {Promise<Object>} - Promesa con la entidad creada
   */
  async crearEntidad(entidad) {
    return apiClient.post('/rcv/entidades', entidad);
  },

  /**
   * Crear entidad automáticamente si no existe
   * @param {string} rut - Número de RUT
   * @param {string} dv - Dígito verificador  
   * @param {string} nombre - Nombre o razón social
   * @returns {Promise<Object>} - Promesa con la entidad creada o existente
   */
  async crearSiNoExiste(rut, dv, nombre) {
    try {
      // Verificar si ya existe
      const existente = await this.buscarPorRut(rut, dv);
      if (existente) {
        return existente;
      }

      // Crear nueva entidad
      const nuevaEntidad = {
        rut: rut.toString(),
        dv: dv.toUpperCase(),
        nombre: nombre.trim(),
        tipo: 'PROVEEDOR',
        activa: true
      };

      return await this.crearEntidad(nuevaEntidad);
    } catch (error) {
      console.error('Error al crear entidad:', error);
      throw new Error(`No se pudo crear la entidad: ${error.message}`);
    }
  }
};